import React, { useCallback, useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import MappingTableRow from '../../TableRow';
import { MappingTypeSimpleBase } from '../Simple';
import ChaserStyledSelect from '../../../../../../components/ChaserStyledSelect';
import { useReadOnly } from '../../readOnlyContext';

export const getConsumedStatuses = statuses => {
  return (statuses || []).flatMap(s => s.csvStatuses || []).map(s => s.csvStatus);
};

export const getConsumedColumns = value => {
  return {
    [value?.columnId]: getConsumedStatuses(value?.statuses),
  };
};

export const MappingTypeStatusStatuses = ({ valueStatuses, fieldStatuses, options, onChange }) => {
  const statusesByChaserStatus = useMemo(
    () =>
      Object.fromEntries(
        (valueStatuses || []).map(status => [
          status.chaserStatus,
          (status.csvStatuses || []).map(csvStatus => ({
            value: csvStatus.csvStatus,
            label: csvStatus.csvStatus,
          })),
        ]),
      ),
    [valueStatuses],
  );

  const onStatusChange = useCallback(
    (chaserStatus, selectValue) => {
      onChange(
        fieldStatuses
          .map(status => {
            if (status.id === chaserStatus) {
              return {
                chaserStatus: status.id,
                csvStatuses: (selectValue || []).map(v => ({ csvStatus: v.value })),
              };
            }
            return (valueStatuses || []).find(s => s.chaserStatus === status.id);
          })
          .filter(Boolean),
      );
    },
    [onChange, valueStatuses, fieldStatuses],
  );

  const readOnly = useReadOnly();

  return (
    <>
      {fieldStatuses.map(status => (
        <MappingTableRow key={status.id} field={status} isNested>
          <ChaserStyledSelect
            as={CreatableSelect}
            isMulti
            value={statusesByChaserStatus[status.id] || []}
            onChange={values => onStatusChange(status.id, values)}
            options={options}
            isDisabled={readOnly}
          />
        </MappingTableRow>
      ))}
    </>
  );
};

const MappingTypeStatus = ({ field, value, availableColumns, onChange }) => {
  const options = useMemo(() => {
    if (!value?.columnId) {
      return [];
    }

    const availableValues = availableColumns[value.columnId]?.availableValues || [];
    return availableValues.map(value => ({ value, label: value }));
  }, [value, availableColumns]);

  const onStatusesChange = useCallback(
    statuses => {
      onChange({ ...value, statuses });
    },
    [onChange, value],
  );

  const onColumnChange = useCallback(
    value => {
      if (!value?.columnId) return onChange(null);
      onChange({ columnId: value.columnId, statuses: [] });
    },
    [onChange],
  );

  return (
    <>
      <MappingTableRow field={field}>
        <MappingTypeSimpleBase
          value={value}
          availableColumns={availableColumns}
          onChange={onColumnChange}
          required={field.required}
        />
      </MappingTableRow>

      {value?.columnId && (
        <MappingTypeStatusStatuses
          valueStatuses={value.statuses}
          fieldStatuses={field.statuses}
          options={options}
          onChange={onStatusesChange}
        />
      )}
    </>
  );
};

export const Component = MappingTypeStatus;
